/**
 * IMEI format — the client half of audit obs #4 (Application Logic Bypass, CWE-840).
 *
 * THE SERVER IS THE CONTROL. This file is not.
 * -------------------------------------------------------------------------
 * The finding was that the Add Device drawer refuses an IMEI shorter than 10
 * digits and the API did not, so the rule fell away the moment a request was
 * edited in a proxy. That hole is closed on the server, in
 * `deviceGuard.checkImeiFormat` / `vtsAssetsCtrl.vehDvceMstrupdt_post`, and it
 * stays closed whether or not this file exists.
 *
 * What this file is for is that the user should learn about a bad IMEI in the
 * drawer, where they can fix it, rather than as an HTTP 400 after submitting.
 * Deleting it would be a UX regression, not a security one — which is exactly
 * the relationship a client-side check should have to its server counterpart.
 *
 * WHY IT IS SHARED RATHER THAN WRITTEN TWICE
 * -------------------------------------------------------------------------
 * Two screens post to POST /drivers/vehicles/Master/Device/update:
 *
 *     add-vehicle-device.component.ts   had `isNaN(v) || v.length < 10`
 *     assign-device.component.ts        had NO IMEI CHECK AT ALL
 *
 * so the "minimum 10 digits" rule was never a control even inside the browser —
 * the second screen sent the same bad value with no proxy involved. One
 * implementation means a third screen either uses it or is visibly missing it.
 *
 * KEEP IN STEP WITH THE SERVER
 * -------------------------------------------------------------------------
 * The bounds below mirror `IMEI_MIN_LEN` / `IMEI_MAX_LEN` in
 * `server/api/modules/drivers/validators/deviceGuard.js`. They must not drift:
 *
 *   - stricter here  -> the drawer refuses a value the API would have accepted,
 *                       and the user cannot enter a device that is legitimate;
 *   - looser here    -> the user gets a 400 they were given no chance to avoid,
 *                       which is the confusing failure this file exists to stop.
 *
 * The bounds are NOT the real IMEI standard (15 digits, Luhn check digit) on
 * purpose. Measured on production, 9,982 of 10,035 registered devices are 15
 * digits but the remainder run 10-19, and those devices are live in the field.
 * A standards-correct rule would lock their owners out of their own records.
 */

/** Mirrors deviceGuard.IMEI_MIN_LEN. */
export const IMEI_MIN_LEN = 10;
/** Mirrors deviceGuard.IMEI_MAX_LEN. Longest value stored in production is 19. */
export const IMEI_MAX_LEN = 20;

/** Mirrors deviceGuard.IMEI_MESSAGE, so the wording is the same whichever side rejects. */
export const IMEI_MESSAGE =
  `Please enter a valid IMEI Number (${IMEI_MIN_LEN} to ${IMEI_MAX_LEN} digits).`;

const IMEI_RE = new RegExp(`^[0-9]{${IMEI_MIN_LEN},${IMEI_MAX_LEN}}$`);

/**
 * Is `value` a well-formed IMEI, by the same rule the API applies?
 *
 * DIGITS ONLY — deliberately stricter than the `isNaN()` test this replaces.
 * isNaN is a numeric-parse check, not a digit check, and accepted values the
 * drawer's own "minimum 10 digits" label does not mean:
 *
 *     '1e10000000'     isNaN false, 10 characters long  -> was accepted
 *     ' 12345678901 '  Number() ignores the padding     -> was accepted
 *     '12345678901.5'  a decimal                        -> was accepted
 *
 * VALIDATES THE STRING, NEVER Number(value). Two ways that goes wrong:
 * `Number('012345678901')` drops the leading zero, and anything past 15 digits
 * exceeds Number.MAX_SAFE_INTEGER and is rounded — so the value checked would
 * not be the value posted. Production holds 17- and 19-digit IMEIs, so this is
 * a real case rather than a theoretical one.
 *
 * Whitespace is REJECTED, not trimmed: trimming would validate one string and
 * submit a different one.
 */
export function isValidImei(value: any): boolean {
  if (value === undefined || value === null) { return false; }
  // Guard the type before String(): an array's toString drops the brackets, so
  // ['12345678901'] would otherwise read as a valid IMEI.
  if (typeof value !== 'string' && typeof value !== 'number') { return false; }
  if (typeof value === 'number' && !isFinite(value)) { return false; }
  return IMEI_RE.test(String(value));
}
